import React from "react";
import { ChartCanvas, Chart } from "react-stockcharts";
import { BarSeries, LineSeries } from "react-stockcharts/lib/series";
import { XAxis, YAxis } from "react-stockcharts/lib/axes";
import { fitWidth } from "react-stockcharts/lib/helper";
import { HoverTooltip } from "react-stockcharts/lib/tooltip";
import { discontinuousTimeScaleProvider } from "react-stockcharts/lib/scale";
import d3 from "d3";
import { timeFormat } from "d3-time-format";
import { last } from "react-stockcharts/lib/utils";
import { compact, includes, orderBy } from "lodash";
import { useDataSelectors } from "../hooks";
import { ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import styled from "styled-components";

const feeFormatter = d3.format(".0%");
const availableFormatter = d3.format(",");
const closeFormatter = d3.format("$.2");
const parseDate = d3.time.format("%Y-%m-%d").parse;

const FEE = "fee";
const AVAILABLE = "available";
const CLOSE = "close";
const VOLUME = "volume";

const computeMargin = (showAvailable, showVolume, showFee, showClose) => ({
  left: ((showAvailable && 60) || 0) + ((showVolume && 60) || 0),
  right: ((showFee && 60) || 0) + ((showClose && 60) || 0),
  top: 30,
  bottom: 30,
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  margin-top: 10px;
`;

export const _StockChart = ({
  historicalData = [],
  priceData = [],
  width,
  ratio,
  showFee,
  setFee,
  showAvailable,
  setAvailable,
  showClose,
  setClose,
  showVolume,
  setVolume,
  country,
}) => {
  const allData = {};

  historicalData.forEach((el) => {
    allData[el.date] = {
      date: parseDate(el.date),
      fee: el.fee != null ? el.fee / 100 : null,
      available: el.available,
    };
  });

  priceData.forEach((el) => {
    allData[el.date] = {
      ...(allData[el.date] || {}),
      date: parseDate(el.date),
      close: el.close,
      volume: el.volume,
    };
  });

  const parsedData = orderBy(allData, ["date"]);

  // Need two elements for chart extents
  if (parsedData.length <= 1) {
    return <div>No data available</div>;
  }

  const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(
    (d) => d.date
  );
  const { data, xScale, xAccessor, displayXAccessor } = xScaleProvider(
    parsedData
  );
  const xExtents = [xAccessor(last(data)), xAccessor(data[0])];

  const margin = computeMargin(showAvailable, showVolume, showFee, showClose);
  const height = 600;

  const toggleValue = [
    showFee ? FEE : null,
    showAvailable ? AVAILABLE : null,
    showClose ? CLOSE : null,
    showVolume ? VOLUME : null,
  ];
  return (
    <>
      {country === "usa" && (
        <StyledToggleButtonGroup
          type="checkbox"
          value={compact(toggleValue)}
          onChange={(newValues) => {
            if (newValues.length === 0) return;
            setFee(includes(newValues, FEE));
            setAvailable(includes(newValues, AVAILABLE));
            setClose(includes(newValues, CLOSE));
            setVolume(includes(newValues, VOLUME));
          }}
        >
          <ToggleButton value={FEE}>Fee</ToggleButton>
          <ToggleButton value={AVAILABLE}>Available</ToggleButton>
          <ToggleButton value={CLOSE}>Close</ToggleButton>
          <ToggleButton value={VOLUME}>Shares Traded</ToggleButton>
        </StyledToggleButtonGroup>
      )}
      <ChartCanvas
        ratio={ratio}
        width={width}
        height={height}
        margin={margin}
        type="hybrid"
        seriesName="Stock Chart"
        data={data}
        xScale={xScale}
        xAccessor={xAccessor}
        displayXAccessor={displayXAccessor}
        xExtents={xExtents}
      >
        <HoverTooltip
          fontSize={14}
          yAccessor={(d) => d.fee}
          tooltipContent={({ currentItem, xAccessor }) => ({
            x: timeFormat("%Y-%m-%d")(xAccessor(currentItem)),
            y: [
              showFee && {
                label: "Fee",
                value: currentItem.fee && feeFormatter(currentItem.fee),
                stroke: "#FF2D04",
              },
              showAvailable && {
                label: "Available",
                value:
                  currentItem.available &&
                  availableFormatter(currentItem.available),
                stroke: "#4682b4",
              },
              showClose && {
                label: "Close",
                value: currentItem.close && closeFormatter(currentItem.close),
                stroke: "#666666",
              },
              showVolume && {
                label: "Volume",
                value:
                  currentItem.volume && availableFormatter(currentItem.volume),
                stroke: "#1a4d00",
              },
            ].filter((row) => row?.value != null),
          })}
        />
        {showFee && (
          <Chart
            id={1}
            yExtents={[(d) => 0, (d) => (d.fee != null ? d.fee : 0)]}
          >
            <XAxis axisAt="bottom" orient="bottom" ticks={5} />
            <YAxis
              axisAt={width - margin.right - margin.left}
              orient="right"
              ticks={5}
              tickFormat={d3.format(".0%")}
              fill="#FF2D04"
              stroke="#FF2D04"
            />
            <LineSeries
              yAccessor={(d) => d.fee}
              stroke="#FF2D04"
              strokeWidth={2}
            />
          </Chart>
        )}
        {showAvailable && (
          <Chart id={2} yExtents={[(d) => 0, (d) => d.available]}>
            <XAxis axisAt="bottom" orient="bottom" ticks={5} />
            <YAxis
              axisAt={0}
              orient="left"
              ticks={5}
              stroke="#4682b4"
              tickFormat={d3.format(".2s")}
            />
            <BarSeries yAccessor={(d) => d.available} fill="#4682b4" />
          </Chart>
        )}
        {showClose && (
          <Chart id={3} yExtents={(d) => d.close}>
            <XAxis axisAt="bottom" orient="bottom" ticks={5} />
            <YAxis
              axisAt={
                width - margin.right - margin.left + ((showFee && 70) || 0)
              }
              orient="right"
              ticks={5}
              tickFormat={d3.format("$.2")}
              stroke="#666666"
            />
            <LineSeries
              yAccessor={(d) => d.close}
              stroke="#666666"
              strokeWidth={2}
            />
          </Chart>
        )}
        {showVolume && (
          <Chart id={4} yExtents={[(d) => 0, (d) => d.volume]}>
            <XAxis axisAt="bottom" orient="bottom" ticks={5} />
            <YAxis
              axisAt={0 - ((showAvailable && margin.left / 2) || 0)}
              orient="left"
              ticks={5}
              stroke="#1a4d00"
              tickFormat={d3.format(".2s")}
            />
            <BarSeries yAccessor={(d) => d.volume} fill="#1a4d00" />
          </Chart>
        )}
      </ChartCanvas>
    </>
  );
};

export default fitWidth(_StockChart);
